import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ThumbsUpMinor } from '@shopify/polaris-icons'
import { Button } from 'src/components/shared/Button'
import { colors } from 'src/theme'
import { CollectedData } from 'src/interfaces/collectedData'
import { ContractorDetails } from 'src/interfaces/contractor'
import { ContractorDetailsModalContext } from '../ContractorDetailsModal.context'
import { ApproveStepModal } from '../approve-step/ApproveStepModal'
import { useModalOpen } from 'src/hooks/useModalOpen'
import { ApproveOnboardingStep } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { canSetDatesOnApprove } from '../approve-step/approveStepUtils'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'

interface Props {
  collectedData: CollectedData
  contractorDetails: ContractorDetails
  isCombinedStep?: boolean
  onAction(): void | Promise<void>
}

const ApproveButton = ({
  collectedData,
  contractorDetails,
  isCombinedStep = false,
  onAction,
}: Props) => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const { triggerStatusDelayedContractorRefresh } = useContext(ContractorDetailsModalContext)
  const { isOpen, open, close } = useModalOpen()

  const canSetDates = useMemo(
    () => canSetDatesOnApprove(collectedData, isCombinedStep),
    [collectedData, isCombinedStep],
  )

  const handleSuccess = useCallback(async () => {
    triggerStatusDelayedContractorRefresh()
    await onAction()
  }, [triggerStatusDelayedContractorRefresh, onAction])

  const handleDirectApprove = useCallback(async () => {
    setIsLoading(true)
    try {
      await createAsyncAction(
        dispatch,
        ApproveOnboardingStep.request({
          stepId: collectedData.collectDataStep.id,
          partnerDriverId: contractorDetails.id,
        }),
      )
      await handleSuccess()
      showToast('Step is approved.')
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [collectedData, contractorDetails, dispatch, handleSuccess])

  const isApprovingWithoutModal = !canSetDates && isLoading

  return (
    <>
      <Button
        onClick={canSetDates ? open : handleDirectApprove}
        fullWidth
        size="small"
        color="green"
        loading={isApprovingWithoutModal}
        disabled={isApprovingWithoutModal}
        endIcon={<ThumbsUpMinor width={20} fill={colors.COLORS_GREEN_500} />}
      >
        Approve
      </Button>
      {canSetDates && (
        <ApproveStepModal
          isOpen={isOpen}
          collectedData={collectedData}
          contractorDetails={contractorDetails}
          isCombinedStep={isCombinedStep}
          onClose={close}
          onSuccess={handleSuccess}
        />
      )}
    </>
  )
}

export default ApproveButton
